import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/podkit/select/Select";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Heading3 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { formatSeconds } from "@/format/time";
import { useGetOrganizationPolicies, useUpdateOrganizationPolicies } from "@/queries/organization-queries";
import { formatError } from "@/utils/errors";
import { Duration, type PartialMessage } from "@bufbuild/protobuf";
import { type OrganizationPolicies } from "gitpod-next-api/gitpod/v1/organization_pb";
import { type FC, useCallback, useMemo } from "react";

type AutoDeleteOption = {
    label: string;
    value: number; // seconds
};

type AutoDeleteArchivedEnvironmentsProps = {
    editable?: boolean;
};

export const AutoDeleteArchivedEnvironments: FC<AutoDeleteArchivedEnvironmentsProps> = ({ editable = true }) => {
    const { data: organizationPolicies, isLoading } = useGetOrganizationPolicies();
    const updateOrganizationPolicies = useUpdateOrganizationPolicies();
    const { toast } = useToast();

    const currentAutoDelete = useMemo(() => {
        const seconds = organizationPolicies?.deleteArchivedEnvironmentsAfter?.seconds;
        return typeof seconds === "bigint" ? Number(seconds) : 0;
    }, [organizationPolicies]);

    const autoDeleteOptions: AutoDeleteOption[] = useMemo(() => {
        const options = [
            { label: "1 day", value: 1 * 24 * 60 * 60 },
            { label: "2 days", value: 2 * 24 * 60 * 60 },
            { label: "3 days", value: 3 * 24 * 60 * 60 },
            { label: "5 days", value: 5 * 24 * 60 * 60 },
            { label: "1 week", value: 7 * 24 * 60 * 60 },
            { label: "2 weeks", value: 14 * 24 * 60 * 60 },
            { label: "4 weeks", value: 28 * 24 * 60 * 60 },
            { label: "Never", value: 0 },
        ];
        if (!options.some((option) => option.value === currentAutoDelete)) {
            options.push({ label: formatSeconds(currentAutoDelete, "long"), value: currentAutoDelete });
        }

        return options;
    }, [currentAutoDelete]);

    const handleAutoDeleteChange = useCallback(
        async (valueString: string) => {
            const value = parseInt(valueString, 10);
            const params: PartialMessage<OrganizationPolicies> = {
                deleteArchivedEnvironmentsAfter: new Duration({
                    seconds: BigInt(value),
                    nanos: 0,
                }),
            };

            try {
                await updateOrganizationPolicies.mutateAsync(params);
                toast({
                    title: "Auto-delete setting updated",
                });
            } catch (error) {
                console.error(error);
                toast({
                    title: "We couldn't save your changes. Sorry about that! Please retry.",
                    description: formatError(error),
                });
            }
        },
        [updateOrganizationPolicies, toast],
    );

    return (
        <div className="flex flex-col gap-3 rounded-lg border border-border-subtle px-5 py-4">
            <SkeletonBlock ready={!isLoading}>
                <div className="flex flex-col gap-0.5">
                    <Heading3 className="text-base">Auto-delete archived environments</Heading3>
                    <Text className="text-sm text-content-secondary">
                        Controls the maximum time an archived environment exists.
                    </Text>
                </div>
                <Select
                    name="auto-delete-archived-environments"
                    value={currentAutoDelete.toString()}
                    onValueChange={handleAutoDeleteChange}
                    disabled={!editable}
                >
                    <SelectTrigger
                        loading={false}
                        className="max-w-[315px]"
                        data-testid="auto-delete-archived-environments-select"
                    >
                        <SelectValue placeholder="Select auto-delete period" />
                    </SelectTrigger>
                    <SelectContent className="w-[var(--radix-select-trigger-width)]">
                        {autoDeleteOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value.toString()}>
                                <div className="flex w-full items-center justify-between">{option.label}</div>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </SkeletonBlock>
        </div>
    );
};
